import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ImmediateOnDemandWaiting from "../../common/immediateOnDemand/ImmediateOnDemandWaiting";
import ImmediateOnDemandThankYou from "../../common/immediateOnDemand/ImmediateOnDemandThankYou";
import {getAppointmentId} from "../../common/immediateOnDemand/matchingUtils";
import useStoppableSound from "../../common/immediateOnDemand/useStoppableSound";
import "../../common/immediateOnDemand/immediateOnDemand.css";

const CONFIRMATION_SECTIONS = [
  {
    key: "iAm",
    label: "I am:",
    items: [
      {
        id: "open",
        text: "Open to this experience",
      },
      {
        id: "respond",
        text: 'Ready to respond to potential evidence with "yes", "no", "possibly" or "I don\'t know"',
      },
      {
        id: "zoom",
        text: "Using a device with the Zoom app already downloaded (No account needed just the app.)",
      },
    ],
  },
  {
    key: "iWill",
    label: "I will:",
    items: [
      {
        id: "quiet",
        text: "Attend in a quiet space where I am not distracted",
      },
      {
        id: "alone",
        text: "Attend alone to enable focus on my loved ones.",
      },
      {
        id: "record",
        text: "Not record without permission from the Medium (any recordings are for private use only)",
      },
    ],
  },
  {
    key: "iUnderstand",
    label: "I understand that:",
    items: [
      {
        id: "guarantees",
        text: "There are no guarantees of who will come through in my session.",
      },
      {
        id: "interpreter",
        text: "I am the best interpreter of the information conveyed my loved ones.",
      },
      {
        id: "recognition",
        text: "Post-session recognition of evidence is normal and may serve a purpose.",
      },
    ],
  },
  {
    key: "iAgree",
    label: "I agree that:",
    items: [
      {
        id: "early",
        text: "I will show up a few minutes early for the session that I schedule.",
      },
      {
        id: "reconfirm",
        text: "I must reconfirm at least 2 hours before through the 24-hour reminder email",
      },
      {
        id: "survey",
        text: "I will complete the emailed post-session survey (checking spam/junk folders).",
      },
      {
        id: "responsibility",
        text: "I take sole responsibility for my interpretations, choices, and actions.",
      },
    ],
  },
];

const CONFIRMATIONS = CONFIRMATION_SECTIONS.reduce(
  (items, section) => items.concat(section.items),
  []
);

const initialChecks = CONFIRMATIONS.reduce((result, item) => {
  result[item.id] = false;
  return result;
}, {});

export default function ClientImmediateOnDemand({ onCancel, embedded = false }) {
  const { t } = useTranslation("clientLive");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skipConfirm = searchParams.get("skip_confirm") === "true";
  const [step, setStep] = useState(skipConfirm ? "waiting" : "confirm");
  const [checks, setChecks] = useState(initialChecks);
  const [matchData, setMatchData] = useState(null);
  const audioRef = useRef(null);

  const allChecked = CONFIRMATIONS.every((item) => checks[item.id]);

  const updateCheck = (id, checked) => {
    setChecks((currentChecks) => ({
      ...currentChecks,
      [id]: checked,
    }));
  };
  

  const updateAllChecks = (checked) => {
    setChecks(
      CONFIRMATIONS.reduce((result, item) => {
        result[item.id] = checked;
        return result;
      }, {})
    );
  };

  const cancelImmediateSession = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate("/appointments");
  };

  // "Exit To Dashboard" in the leave-queue popup: just leave the queue and
  // go to the client's shortcuts/hub page. Booking a future (donated)
  // session is a separate, explicit action now handled by the popup's own
  // "Book Future Session" button in ImmediateOnDemandWaiting.js.
  const exitQueueToShortcuts = () => {
    navigate("/shortcuts");
  };

  // Confirmation sound (ringNew.wav, ~2 min): stops on any click, key press,
  // hover over a button/link, tab switch, or leaving the screen.
  useStoppableSound(audioRef, step === "thankyou");
  const handleMatched = useCallback((data) => {
    const appointmentId = getAppointmentId(data);
    if (appointmentId) {
      localStorage.setItem("immediate_session_appointment_id", appointmentId);
    }
    setMatchData(data);
    setStep("thankyou");
  }, []);

  const getMatchedPath = useCallback((data) => {
    const appointmentId = data && data.appointmentId;
    return appointmentId
      ? `/thankyou?from=immediate-session&appointment_id=${appointmentId}`
      : "/thankyou?from=immediate-session";
  }, []);

  if (step === "waiting") {
    return (
      <>
       <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />
      <ImmediateOnDemandWaiting
        participantRole="client"
        exitPath="/appointments"
        onExit={embedded ? (onCancel || (() => navigate("/appointments"))) : exitQueueToShortcuts}
        onMatched={handleMatched}
        embedded={embedded}
        title={t("waiting.title")}
        matchedPath={getMatchedPath}
        showBookFutureSession={skipConfirm}
      />
      </>
    );
  }

  if (step === "thankyou") {
    return (
      <>
      <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />
      <ImmediateOnDemandThankYou
        matchData={matchData}
        participantRole="client"
        embedded={embedded}
        // Standalone /go-on-demand-immediate-session route: show the waiting-room
        // countdown + Click Here, and hide "Go to My Appointments".
        // Embedded (My Appointments page): keep the original behaviour.
        showWaitingRoom={!embedded}
        onDone={embedded ? onCancel || (() => navigate("/appointments")) : undefined}
      />
      </>
    );
  }

  return (
    <div className={`immediate-ondemand-page${embedded ? " immediate-ondemand-page--embedded" : ""}`}>
      <div className="immediate-ondemand-card">
        <h2>{t("confirm.title")}</h2>

        <label className="immediate-ondemand-check immediate-ondemand-check--all">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={(event) => updateAllChecks(event.target.checked)}
          />
          <span>{t("confirm.agreeAll")}</span>
        </label>

        {CONFIRMATION_SECTIONS.map((section) => (
          <div className="immediate-ondemand-section" key={section.label}>
            <h3>{t(`confirm.sections.${section.key}`)}</h3>
            {section.items.map((item) => (
              <label className="immediate-ondemand-check" key={item.id}>
                <input
                  type="checkbox"
                  checked={checks[item.id]}
                  onChange={(event) => updateCheck(item.id, event.target.checked)}
                />
                <span>{t(`confirm.items.${item.id}`)}</span>
              </label>
            ))}
          </div>
        ))}

        <div className="immediate-ondemand-actions">
          <button
            type="button"
            className="immediate-ondemand-secondary"
            onClick={cancelImmediateSession}
          >
            {t("confirm.cancel")}
          </button>
          <button
            type="button"
            className="immediate-ondemand-primary"
            onClick={() => setStep("waiting")}
            disabled={!allChecked}
          >
            {t("confirm.next")}
          </button>
        </div>
      </div>
    </div>
  );
}
